import type { KeyboardEvent } from 'react';

import { pt } from '../copy';

const MAX_PROMPT_LENGTH = 2000;
const PROMPT_WARNING_THRESHOLD = 1800;

type PromptBarProps = {
  location: string;
  onLocationChange: (v: string) => void;
  prompt: string;
  onPromptChange: (v: string) => void;
  onSubmit: () => void;
  disabled: boolean;
  loading: boolean;
};

export default function PromptBar({
  location,
  onLocationChange,
  prompt,
  onPromptChange,
  onSubmit,
  disabled,
  loading,
}: PromptBarProps) {
  const promptLength = prompt.length;
  const isPromptNearLimit = promptLength >= PROMPT_WARNING_THRESHOLD;
  const isPromptTooLong = promptLength > MAX_PROMPT_LENGTH;
  const isSubmitDisabled = disabled || loading || isPromptTooLong;

  const handlePromptKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || isSubmitDisabled) {
      return;
    }

    event.preventDefault();
    onSubmit();
  };

  return (
    <div className="space-y-4 rounded-3xl border border-white/10 bg-white/5 p-4 backdrop-blur-xl">
      <div className="space-y-2">
        <label className="text-sm font-medium text-white/80" htmlFor="location-input">
          {pt.locationLabel}
        </label>
        <textarea
          className="min-h-20 w-full resize-none rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-white/25"
          id="location-input"
          placeholder={pt.locationPlaceholder}
          rows={2}
          value={location}
          onChange={(event) => onLocationChange(event.target.value)}
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-white/80" htmlFor="prompt-input">
          {pt.promptLabel}
        </label>
        <textarea
          className="min-h-32 w-full resize-y rounded-2xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-white/25"
          id="prompt-input"
          placeholder={pt.promptPlaceholder}
          rows={5}
          value={prompt}
          onChange={(event) => onPromptChange(event.target.value)}
          onKeyDown={handlePromptKeyDown}
        />
        <div className="space-y-1">
          <p
            className={[
              'text-right text-xs',
              isPromptNearLimit ? 'text-amber-300' : 'text-white/45',
            ].join(' ')}
          >
            {pt.charCounterLabel(promptLength, MAX_PROMPT_LENGTH)}
          </p>
          {isPromptTooLong ? (
            <p className="text-right text-xs text-amber-200">{pt.promptTooLong}</p>
          ) : null}
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-black/20 px-3 py-3">
        <p className="text-xs text-white/55">{pt.promptHelper}</p>
        <button
          className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-black transition hover:bg-white/90 disabled:cursor-not-allowed disabled:bg-white/20 disabled:text-white/45"
          disabled={isSubmitDisabled}
          type="button"
          onClick={onSubmit}
        >
          {loading ? (
            <span
              className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-black/30 border-t-black"
              aria-hidden="true"
            />
          ) : null}
          <span>{loading ? pt.generateCtaLoading : pt.generateCta}</span>
        </button>
      </div>
    </div>
  );
}
